import * as Sentry from "@sentry/react-native";
import Head from "expo-router/head";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { z } from "zod";

import {
  FormProvider,
  FormSelect,
  useFormWrapper,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import type {
  ClosingTime,
  ClosingTimeRequest,
  ClosingTimeRequestAction,
  SchoolClass,
} from "@luna-restaurant/school-api";
import {
  NoticeBox,
  PageContainer,
  ThemedFeedback,
  ThemedHeadlineText,
  ThemedLoader,
  ThemedTable,
  type ThemedTableColumn,
  type ThemedTableHeaderColumn,
  ThemedTitle,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { ContactBanner } from "@/components/ContactBanner";
import { LockClockIcon } from "@/components/LockClockIcon.web";
import { SecondaryButton } from "@/components/SecondaryButton";
import { StatusBannerHost } from "@/components/StatusBannerHost";
import {
  ClosingTimeForm,
  type ClosingTimeFormValues,
} from "@/components/forms/ClosingTimeForm";
import { ConfirmDeleteClosingTimeRequestModal } from "@/components/modals/ConfirmDeleteClosingTimeRequestModal";
import {
  DeleteClosingTimeModal,
  type DeleteClosingTimeModalValues,
} from "@/components/modals/DeleteClosingTimeModal";
import { EditClosingTimeModal } from "@/components/modals/EditClosingTimeModal";
import { useClosingTimeRequestMutation } from "@/hooks/useClosingTimeRequestMutation";
import { useClosingTimeRequests } from "@/hooks/useClosingTimeRequests";
import { useClosingTimes } from "@/hooks/useClosingTimes";
import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRequest } from "@/hooks/useRequest";
import { useScrollIntoView } from "@/hooks/useScrollIntoView";
import { useStatusBanner } from "@/hooks/useStatusBanner";
import {
  isClosingTimeRequestDateRangeAllowed,
  isClosingTimeRequestStartDateAllowed,
} from "@/utils/closingTimeRequestDateRules";
import { formatBerlinDateOnly } from "@/utils/closingTimes/berlinDateOnly";
import {
  type CloseTimesDisplayRecord,
  type ClosingTimeRequestAnomaly,
  type PendingRequestDisplayRecord,
  buildCloseTimesDisplayModel,
} from "@/utils/closingTimes/displayRecords";
import {
  buildCreateClosingTimeRequestPayload,
  buildDeleteClosingTimeRequestPayload,
  buildUpdateClosingTimeRequestPayload,
  hasClosingTimeEditChanges,
} from "@/utils/closingTimes/requestPayloads";
import { getStatusBannerErrorMessage } from "@/utils/getStatusBannerErrorMessage";

const REQUEST_ACTION_CREATE = "CREATE";
const REQUEST_ACTION_UPDATE = "UPDATE";
const REQUEST_ACTION_DELETE = "DELETE";

const getClosingTimeRequestAnomalyKey = (
  anomaly: ClosingTimeRequestAnomaly,
): string =>
  JSON.stringify([anomaly.reason, anomaly.requestIri, anomaly.closingTimeIri]);

function ClosingTimeRow({
  record,
  formatDate,
  onEdit,
  onDelete,
  onCancel,
  editLabel,
  deleteLabel,
  cancelLabel,
  actionBadgeLabel,
  schoolLabel,
  schoolClassName,
  isFirst,
  isLast,
  isMutating,
}: {
  record: CloseTimesDisplayRecord;
  formatDate: (value?: string | null) => string;
  onEdit: () => void;
  onDelete: () => void;
  onCancel: () => void;
  editLabel: string;
  deleteLabel: string;
  cancelLabel: string;
  actionBadgeLabel?: string;
  schoolLabel: string;
  schoolClassName?: string;
  isFirst?: boolean;
  isLast?: boolean;
  isMutating: boolean;
}) {
  const isPendingRequest = record.kind !== "closing-time";
  const isModificationAllowed =
    record.kind === "closing-time" &&
    isClosingTimeRequestStartDateAllowed(record.closingTime.startDate);
  const rowActions =
    isPendingRequest && actionBadgeLabel
      ? [
          {
            label: actionBadgeLabel,
            onPress: () => undefined,
            disabled: true,
          },
          {
            label: cancelLabel,
            icon: "restore" as const,
            onPress: onCancel,
            disabled: isMutating,
          },
        ]
      : record.kind === "closing-time"
        ? [
            {
              label: editLabel,
              icon: "pencil" as const,
              onPress: onEdit,
              disabled: isMutating || !isModificationAllowed,
            },
            {
              label: deleteLabel,
              icon: "delete" as const,
              onPress: onDelete,
              disabled: isMutating || !isModificationAllowed,
            },
          ]
        : [];

  const actionColumn: ThemedTableColumn =
    rowActions.length > 0
      ? {
          type: "action",
          actions: rowActions,
        }
      : {
          type: "text",
          label: "",
        };

  const labelWithStatus = record.name || "-";

  const columns: ThemedTableColumn[] = [
    { type: "text", label: formatDate(record.startDate) },
    { type: "text", label: formatDate(record.endDate) },
    { type: "text", label: record.editedBy || "-" },
    {
      type: "text",
      label: schoolClassName ?? schoolLabel,
    },
    { type: "text", label: labelWithStatus },
    actionColumn,
  ];

  return (
    <ThemedTable.Row columns={columns} isFirst={isFirst} isLast={isLast} />
  );
}

const ClosingTimeSchema = z.object({
  scopeOrClassId: z.string(),
});
const DEFAULT_SCOPE_VALUE = "school";

export default function CloseTimesScreen() {
  const { fonts } = useCustomTheme();
  const { t, i18n } = useTranslation();
  const { currentUser } = useCurrentUser();
  const request = useRequest();

  const pendingRequestActionLabelMap: Record<ClosingTimeRequestAction, string> =
    {
      [REQUEST_ACTION_CREATE]: t("closeTimes.table.row.badges.createPending"),
      [REQUEST_ACTION_UPDATE]: t("closeTimes.table.row.badges.updatePending"),
      [REQUEST_ACTION_DELETE]: t("closeTimes.table.row.badges.deletePending"),
    };

  const formSectionRef = useRef<View | null>(null);
  const reportedAnomalyKeysRef = useRef(new Set<string>());
  const scrollToForm = useScrollIntoView(formSectionRef);
  const closeTimesOverviewHeadlineRef = useRef<View | null>(null);
  const scrollToCloseTimesOverview = useScrollIntoView(
    closeTimesOverviewHeadlineRef,
  );
  const [selectedScope, setSelectedScope] =
    useState<string>(DEFAULT_SCOPE_VALUE);

  const { showStatusBanner } = useStatusBanner();

  const setErrorStatusBanner = useCallback(
    (error: unknown) => {
      showStatusBanner({
        type: "error",
        message: getStatusBannerErrorMessage(error, t),
        dismissible: true,
        dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
        autoDismissAfterSeconds: 20,
      });
    },
    [showStatusBanner, t],
  );

  const form = useFormWrapper<typeof ClosingTimeSchema>({
    schema: ClosingTimeSchema,
    defaultValues: {
      scopeOrClassId: DEFAULT_SCOPE_VALUE,
    },
    mode: "onSubmit",
  });

  const { subscribe } = form;

  useEffect(() => {
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        setSelectedScope(values.scopeOrClassId);
      },
    });

    return () => callback();
  }, [subscribe]);

  const [editingRecord, setEditingRecord] = useState<ClosingTime | null>(null);
  const [deletingRecord, setDeletingRecord] = useState<ClosingTime | null>(
    null,
  );
  const [deletingRequestRecord, setDeletingRequestRecord] =
    useState<PendingRequestDisplayRecord | null>(null);
  const { isMutating, runMutation } =
    useClosingTimeRequestMutation(setErrorStatusBanner);

  const { items: schoolClasses } = useCollection<SchoolClass>({
    queryKey: ["school-classes", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/classes` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    staleTime: 5 * 60 * 1000,
  });

  // Build merged dropdown options for both filter and form
  const schoolClassOptions = useMemo(
    () => [
      {
        label: t("closeTimes.form.scopeOptions.school"),
        value: DEFAULT_SCOPE_VALUE,
      },
      ...schoolClasses.map((c) => ({ label: c.name, value: c["@id"] })),
    ],
    [schoolClasses, t],
  );

  const schoolClassById = useMemo(() => {
    const map = new Map<string, SchoolClass>();
    for (const schoolClass of schoolClasses) {
      map.set(schoolClass["@id"], schoolClass);
    }
    return map;
  }, [schoolClasses]);

  const editingFormValues = useMemo(() => {
    if (!editingRecord) return null;
    // If editingRecord.schoolClass exists, use its @id, else use "school"
    const scopeOrClassId = editingRecord?.schoolClass ?? "school";
    return {
      editor: editingRecord.editedBy ?? "",
      closingNotice: editingRecord.name,
      dateFrom: editingRecord.startDate,
      dateTo: editingRecord.endDate,
      scopeOrClassId,
    } satisfies Partial<ClosingTimeFormValues>;
  }, [editingRecord]);

  const editSchoolClassOptions = useMemo(() => {
    if (!editingRecord) {
      return schoolClassOptions;
    }

    const lockedScopeOrClassId =
      editingRecord.schoolClass ?? DEFAULT_SCOPE_VALUE;
    const selectedOption = schoolClassOptions.find(
      (option) => option.value === lockedScopeOrClassId,
    );

    if (selectedOption) {
      return [selectedOption];
    }

    return [
      {
        label: t("closeTimes.form.scopeOptions.school"),
        value: DEFAULT_SCOPE_VALUE,
      },
    ];
  }, [editingRecord, schoolClassOptions, t]);

  const deletingFormValues = useMemo(() => {
    if (!deletingRecord) return null;

    const scopeOrClassId = deletingRecord.schoolClass ?? DEFAULT_SCOPE_VALUE;
    return {
      editor: deletingRecord.editedBy ?? "",
      closingNotice: deletingRecord.name ?? "",
      dateFrom: deletingRecord.startDate ?? "",
      dateTo: deletingRecord.endDate ?? "",
      scopeOrClassId,
    } satisfies Partial<DeleteClosingTimeModalValues>;
  }, [deletingRecord]);

  const deleteSchoolClassOptions = useMemo(() => {
    if (!deletingRecord) {
      return schoolClassOptions;
    }

    const lockedScopeOrClassId =
      deletingRecord.schoolClass ?? DEFAULT_SCOPE_VALUE;
    const selectedOption = schoolClassOptions.find(
      (option) => option.value === lockedScopeOrClassId,
    );

    if (selectedOption) {
      return [selectedOption];
    }

    return [
      {
        label: t("closeTimes.form.scopeOptions.school"),
        value: DEFAULT_SCOPE_VALUE,
      },
    ];
  }, [deletingRecord, schoolClassOptions, t]);

  const selectedClassIri = useMemo(() => {
    if (selectedScope === DEFAULT_SCOPE_VALUE) {
      return null;
    }
    return selectedScope;
  }, [selectedScope]);

  const closeTimesQuery = useClosingTimes({
    forSchoolClass: selectedScope === DEFAULT_SCOPE_VALUE ? false : undefined,
    schoolClassIri: selectedClassIri ?? undefined,
    enabled: selectedScope === DEFAULT_SCOPE_VALUE || Boolean(selectedClassIri),
  });

  const closeTimeRequestsQuery = useClosingTimeRequests({
    forSchoolClass: selectedScope === DEFAULT_SCOPE_VALUE ? false : undefined,
    schoolClassIri: selectedClassIri ?? undefined,
    enabled: selectedScope === DEFAULT_SCOPE_VALUE || Boolean(selectedClassIri),
  });

  const closeTimesDisplayModel = useMemo(
    () =>
      buildCloseTimesDisplayModel(
        closeTimesQuery.items,
        closeTimeRequestsQuery.items,
      ),
    [closeTimesQuery.items, closeTimeRequestsQuery.items],
  );
  const closeTimes = closeTimesDisplayModel.records;

  const areCloseTimesSettled =
    !closeTimesQuery.isLoading &&
    !closeTimesQuery.isFetchingNextPage &&
    !closeTimesQuery.hasNextPage;
  const areCloseTimeRequestsSettled =
    !closeTimeRequestsQuery.isLoading &&
    !closeTimeRequestsQuery.isFetchingNextPage &&
    !closeTimeRequestsQuery.hasNextPage;

  const isError = closeTimesQuery.isError || closeTimeRequestsQuery.isError;
  const isLoading =
    !isError &&
    (!areCloseTimesSettled ||
      !areCloseTimeRequestsSettled ||
      (selectedScope !== DEFAULT_SCOPE_VALUE && !selectedClassIri));
  const canReportRequestAnomalies =
    !isError &&
    areCloseTimesSettled &&
    areCloseTimeRequestsSettled &&
    !closeTimesQuery.isFetching &&
    !closeTimeRequestsQuery.isFetching;

  useEffect(() => {
    if (!canReportRequestAnomalies) {
      return;
    }

    for (const anomaly of closeTimesDisplayModel.anomalies) {
      const anomalyKey = getClosingTimeRequestAnomalyKey(anomaly);
      if (reportedAnomalyKeysRef.current.has(anomalyKey)) {
        continue;
      }

      reportedAnomalyKeysRef.current.add(anomalyKey);
      Sentry.captureMessage("Invalid closing-time request relationship", {
        level: "warning",
        extra: anomaly,
      });
    }
  }, [canReportRequestAnomalies, closeTimesDisplayModel.anomalies]);

  const refetch = async () => {
    await Promise.all([
      closeTimesQuery.refetch(),
      closeTimeRequestsQuery.refetch(),
    ]);
  };

  const showFilter = schoolClassOptions.length > 1;

  const emptyStateText = useMemo(() => {
    if (selectedScope === DEFAULT_SCOPE_VALUE) {
      return t("closeTimes.noSchoolCloseTimes.text");
    }
    return t("closeTimes.noClassCloseTimes.text");
  }, [selectedScope, t]);

  const formatDate = (value?: string | null) => {
    if (!value) {
      return "-";
    }

    return formatBerlinDateOnly(value, i18n.language) ?? value;
  };

  const handleEditSubmit = async (values: ClosingTimeFormValues) => {
    if (!editingRecord || isMutating) return;
    if (
      !isClosingTimeRequestStartDateAllowed(editingRecord.startDate) ||
      !isClosingTimeRequestDateRangeAllowed(values.dateFrom, values.dateTo)
    ) {
      return;
    }

    if (!hasClosingTimeEditChanges(values, editingRecord)) {
      return;
    }

    const closingTime = editingRecord;
    await runMutation(
      async () => {
        const payload = buildUpdateClosingTimeRequestPayload(
          closingTime,
          values,
        );

        await request<ClosingTimeRequest>(
          `${currentUser?.school}/closing-time-requests`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/ld+json",
            },
            body: JSON.stringify(payload),
          },
        );
        showStatusBanner({
          type: "success",
          message: t("closeTimes.successBanner.editRequestMessage", {
            dateFrom: formatDate(values.dateFrom),
            dateTo: formatDate(values.dateTo),
          }),
          onViewPress: scrollToCloseTimesOverview,
          viewButtonLabel: t("common.form.successBanner.viewLabel"),
          dismissible: true,
          dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
          autoDismissAfterSeconds: 20,
        });
        setEditingRecord(null);
        await refetch();
      },
      { rethrow: true },
    );
  };

  const handleDeleteConfirm = async (values: DeleteClosingTimeModalValues) => {
    if (!deletingRecord || isMutating) return;
    if (!isClosingTimeRequestStartDateAllowed(deletingRecord.startDate)) {
      return;
    }

    const closingTime = deletingRecord;
    await runMutation(async () => {
      const payload = buildDeleteClosingTimeRequestPayload(
        closingTime,
        values.editor,
      );

      await request<ClosingTimeRequest>(
        `${currentUser?.school}/closing-time-requests`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/ld+json",
          },
          body: JSON.stringify(payload),
        },
      );
      showStatusBanner({
        type: "success",
        message: t("closeTimes.successBanner.deleteRequestMessage", {
          dateFrom: formatDate(closingTime.startDate),
          dateTo: formatDate(closingTime.endDate),
        }),
        onViewPress: scrollToCloseTimesOverview,
        viewButtonLabel: t("common.form.successBanner.viewLabel"),
        dismissible: true,
        dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
        autoDismissAfterSeconds: 20,
      });
      setDeletingRecord(null);
      await refetch();
    });
  };

  const handleCancelRequest = async (
    closingTimeRequestId: number,
  ): Promise<boolean> => {
    if (isMutating) return false;

    const result = await runMutation(async () => {
      await request<ClosingTimeRequest>(
        `${currentUser?.school}/closing-time-requests/${closingTimeRequestId}`,
        {
          method: "DELETE",
        },
      );
      showStatusBanner({
        type: "success",
        message: t("closeTimes.successBanner.cancelRequestMessage"),
        dismissible: true,
        dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
        autoDismissAfterSeconds: 20,
      });
      await refetch();
    });

    return result.ok;
  };

  const handlePendingRequestDeleteConfirm = async () => {
    if (!deletingRequestRecord) {
      return;
    }

    const didDelete = await handleCancelRequest(
      deletingRequestRecord.request.id,
    );
    if (didDelete) {
      setDeletingRequestRecord(null);
    }
  };

  const handleCreateCloseTimeSubmit = async (values: ClosingTimeFormValues) => {
    if (isMutating) return;
    if (!isClosingTimeRequestDateRangeAllowed(values.dateFrom, values.dateTo)) {
      return;
    }
    const submittedScope = values.scopeOrClassId;
    await runMutation(
      async () => {
        const payload = buildCreateClosingTimeRequestPayload(
          values,
          DEFAULT_SCOPE_VALUE,
        );

        await request<ClosingTimeRequest>(
          `${currentUser?.school}/closing-time-requests`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/ld+json",
            },
            body: JSON.stringify(payload),
          },
        );
        showStatusBanner({
          type: "success",
          message: t("closeTimes.successBanner.createRequestMessage", {
            dateFrom: formatDate(values.dateFrom),
            dateTo: formatDate(values.dateTo),
          }),
          onViewPress: scrollToCloseTimesOverview,
          viewButtonLabel: t("common.form.successBanner.viewLabel"),
          dismissible: true,
          dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
          autoDismissAfterSeconds: 20,
        });
        form.setValue("scopeOrClassId", submittedScope);
        await refetch();
        scrollToForm();
      },
      { rethrow: true },
    );
  };

  const header = (
    <>
      <ThemedTitle
        subtitle={t("common.overviewOverYour")}
        title={t("closeTimes.title")}
        customIcon={<LockClockIcon />}
      />
      <StatusBannerHost />
    </>
  );

  const headerColumns: ThemedTableHeaderColumn[] = [
    {
      width: 21,
      parts: [
        { label: t("closeTimes.table.header.from"), style: fonts.regular },
      ],
      style: fonts.bold,
    },
    {
      width: 21,
      parts: [
        { label: t("closeTimes.table.header.until"), style: fonts.regular },
      ],
      style: fonts.bold,
    },
    {
      label: t("closeTimes.table.header.nameOfCreator"),
      width: 37,
    },
    {
      label: t("closeTimes.table.header.schoolOrClass"),
      width: 37,
    },
    {
      label: t("closeTimes.table.header.displayNameOnMealPlan"),
      width: 59,
    },
    {
      label: t("closeTimes.table.header.actions"),
      width: 32,
    },
  ];

  const noteComponent = (
    <>
      <NoticeBox
        title={t("common.notice")}
        textContent={t("closeTimes.createCloseTimeNotice")}
      />
      <Text style={[styles.requiredFieldsHint, fonts.regular]}>
        {t("common.form.requiredFieldsHint")}
      </Text>
    </>
  );

  return (
    <View>
      <Head>
        <title>{t("titles.closingTimes")}</title>
      </Head>
      <PageContainer header={header} style={styles.pageContainer}>
        <View ref={formSectionRef} style={styles.sectionCloseTimesForm}>
          <ThemedHeadlineText
            type={t("closeTimes.type.new")}
            headline={t("closeTimes.createCloseTime")}
            textContent={t("closeTimes.createCloseTimeText")}
          />
          <ClosingTimeForm
            schoolClassOptions={schoolClassOptions}
            noteComponent={noteComponent}
            reserveErrorSpace={false}
            actionsNotice={
              <Text style={[styles.reviewNoticeText, fonts.regular]}>
                {t("closeTimes.form.reviewNotice")}
              </Text>
            }
            dropdownStyle={styles.formContainer}
            onSubmit={handleCreateCloseTimeSubmit}
          />
        </View>

        <View style={styles.sectionCloseTimesOverview}>
          <View ref={closeTimesOverviewHeadlineRef}>
            <ThemedHeadlineText
              type={t("closeTimes.type.current")}
              headline={t("closeTimes.closeTimeOverview")}
              textContent={t("closeTimes.closeTimeOverviewText")}
            />
          </View>

          <View>
            <FormProvider form={form}>
              {showFilter ? (
                <View style={[styles.formContainer, styles.formSelectPadding]}>
                  <FormSelect
                    icon={{
                      iconSet: "material-community",
                      icon: "school-outline",
                    }}
                    clearable={false}
                    name="scopeOrClassId"
                    options={schoolClassOptions}
                    placeholder={t("closeTimes.form.scopePlaceholder")}
                  />
                </View>
              ) : null}
              {isLoading ? (
                <View style={styles.listStateContainer}>
                  <ThemedLoader spinnerSize={50} />
                </View>
              ) : isError ? (
                <ThemedFeedback
                  type="error"
                  iconName="error-outline"
                  subtitle={t("closeTimes.error.subtitle")}
                  title={t("closeTimes.error.title")}
                  text={t("closeTimes.error.text")}
                  description={t("closeTimes.error.description")}
                  onRetry={refetch}
                  retryButtonLabel={t("closeTimes.error.retryButtonLabel")}
                />
              ) : closeTimes.length === 0 ? (
                <View style={styles.noCloseTimesContainer}>
                  <Text style={[styles.noCloseTimesText, fonts.regular]}>
                    {emptyStateText}
                  </Text>
                  <SecondaryButton
                    label={t("closeTimes.newCloseTimeButtonText")}
                    iconName="plus"
                    iconPosition="left"
                    onPress={scrollToForm}
                  />
                </View>
              ) : (
                <View style={styles.tableContainer}>
                  <ThemedTable headerColumns={headerColumns}>
                    {closeTimes.map((record) => {
                      const closingTime =
                        record.kind === "pending-create-request"
                          ? undefined
                          : record.closingTime;
                      const request =
                        record.kind === "closing-time"
                          ? undefined
                          : record.request;
                      const actionBadgeLabel = request
                        ? pendingRequestActionLabelMap[request.action]
                        : undefined;
                      const schoolClassName = record.schoolClass
                        ? schoolClassById.get(record.schoolClass)?.name
                        : undefined;

                      return (
                        <ClosingTimeRow
                          key={record.key}
                          record={record}
                          formatDate={formatDate}
                          onEdit={() => {
                            if (!closingTime) return;
                            setEditingRecord(closingTime);
                          }}
                          onDelete={() => {
                            if (!closingTime) return;
                            setDeletingRecord(closingTime);
                          }}
                          onCancel={() => {
                            if (record.kind === "closing-time") return;
                            setDeletingRequestRecord(record);
                          }}
                          editLabel={t("closeTimes.table.row.actions.edit")}
                          deleteLabel={t("closeTimes.table.row.actions.delete")}
                          cancelLabel={t("closeTimes.table.row.actions.cancel")}
                          actionBadgeLabel={actionBadgeLabel}
                          schoolLabel={t("closeTimes.form.scopeOptions.school")}
                          schoolClassName={schoolClassName}
                          isMutating={isMutating}
                        />
                      );
                    })}
                  </ThemedTable>
                </View>
              )}
            </FormProvider>
          </View>
        </View>
      </PageContainer>
      <ContactBanner />
      <EditClosingTimeModal
        schoolClassOptions={editSchoolClassOptions}
        visible={Boolean(editingRecord)}
        closingTime={editingFormValues}
        onDismiss={() => setEditingRecord(null)}
        onSubmit={handleEditSubmit}
      />
      <DeleteClosingTimeModal
        schoolClassOptions={deleteSchoolClassOptions}
        visible={Boolean(deletingRecord)}
        closingTime={deletingFormValues}
        onDismiss={() => setDeletingRecord(null)}
        onConfirm={handleDeleteConfirm}
        isSubmitting={isMutating}
      />
      <ConfirmDeleteClosingTimeRequestModal
        visible={Boolean(deletingRequestRecord)}
        requestAction={deletingRequestRecord?.request.action}
        onDismiss={() => {
          if (isMutating) return;
          setDeletingRequestRecord(null);
        }}
        onConfirm={() => {
          void handlePendingRequestDeleteConfirm();
        }}
        isSubmitting={isMutating}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    gap: 120,
    paddingTop: 32,
  },
  sectionCloseTimesForm: {
    paddingTop: 32,
    gap: 64,
  },
  sectionCloseTimesOverview: {
    gap: 64,
  },
  noCloseTimesContainer: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 32,
  },
  noCloseTimesText: {
    fontSize: 32,
    lineHeight: 40,
  },
  listStateContainer: {
    minHeight: 300,
  },
  tableContainer: {
    marginBottom: 32,
  },
  requiredFieldsHint: {
    fontSize: 16,
    lineHeight: 20.8,
    marginVertical: 16,
  },
  reviewNoticeText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  formContainer: { width: "50%", minWidth: 270, marginLeft: "auto" },
  formSelectPadding: { paddingLeft: 16 },
});
